import { Project, TaskId } from '@/model/types'
import { checkPresent } from '@/ts_utils'

const META_TASK_ID = 'meta'
const WORK_DAYS = new Set([1, 2, 3, 4, 5])
const WORK_HOURS = new Set([9, 10, 11, 12, 14, 15, 16, 17])

function comparePriorities (project: Project, tid1: TaskId, tid2: TaskId): number {
  const t1 = project.tasks[tid1]
  const t2 = project.tasks[tid2]

  // Compare priority values first.
  if (t1.priority === null && t2.priority === null) {
    // Fall through to comparing ID.
  } else if (t1.priority === null) {
    return 1
  } else if (t2.priority === null) {
    return -1
  } else {
    const cmp = t1.priority - t2.priority
    if (cmp !== 0) return cmp
  }

  // Compare IDs if priorities are equal to break ties in a predictable manner.
  // Use comparison operators to get a locale-independent comparison.
  if (tid2 < tid1) {
    return -1
  } else if (tid2 > tid1) {
    return 1
  } else {
    return 0
  }
}

function makeDate (start: Date, duration: number): Date {
  const copy = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
    // Round up to the next hour
    start.getHours() + (
      (
        start.getMilliseconds() > 0 ||
        start.getSeconds() > 0 ||
        start.getMinutes() > 0
      ) ? 1 : 0
    )
  )

  // TODO: Optimize
  while (duration > 0) {
    if (duration > WORK_HOURS.size) {
      duration -= WORK_HOURS.size
      do {
        copy.setDate(copy.getDate() + 1)
      } while (!WORK_DAYS.has(copy.getDay()))
    } else {
      duration -= 1
      do {
        copy.setHours(copy.getHours() + 1)
      } while (
        !WORK_HOURS.has(copy.getHours()) ||
        !WORK_DAYS.has(copy.getDay())
      )
    }
  }
  return copy
}

export function solve (project: Project, begin: Date = new Date()) {
  const incompleteTasks = new Set(
    Object.entries(project.tasks)
      .filter(([/* id */, task]) => task.completedAt === null)
      .map(([id]) => id)
  )

  const allPrereqs = new Map<TaskId, Set<TaskId>>()

  // Map out direct prerequisites
  for (const dep of Object.values(project.dependencies)) {
    let prereqs = allPrereqs.get(dep.dependent)
    if (!prereqs) {
      prereqs = new Set<TaskId>()
      allPrereqs.set(dep.dependent, prereqs)
    }
    prereqs.add(dep.prereq)
  }

  // Expand prereqs into set of all transitive prereqs by iterating over all
  // tasks and expanding all prerequisite lists containing that prerequisite
  // with the prerequisites of that task.
  allPrereqs.forEach((v0, k0) => {
    allPrereqs.forEach(v1 => {
      if (v1.has(k0)) {
        v0.forEach(v => v1.add(v))
      }
    })
  })

  // Remove references to any completed tasks here after expanding all
  // dependencies so that even if a completed task has an incomplete
  // prerequisite, we'll count it.
  allPrereqs.forEach((prereqs, id) => {
    if (incompleteTasks.has(id)) {
      const incompletePrereqs = new Set(Array.from(prereqs)
        .filter((prereq) => incompleteTasks.has(prereq)))
      allPrereqs.set(id, incompletePrereqs)
    } else {
      allPrereqs.delete(id)
    }
  })

  // Add in a fake metatask and mark it as dependent on everything.
  if (incompleteTasks.has(META_TASK_ID)) throw Error('Task ID collision')
  allPrereqs.set(META_TASK_ID, incompleteTasks)

  function taskDuration (id: TaskId): number | null {
    if (id === META_TASK_ID) return 0
    const task = project.tasks[id]
    checkPresent(task)
    return task.duration
  }

  let time = 0
  const solvedAt = new Map<TaskId, number>()

  // Returns if all necessary data was present (e.g. durations for all
  // transitive prerequisites) to figure out when the task will complete.
  function solveTask (t: TaskId): boolean {
    if (solvedAt.has(t)) return true

    const prereqs = allPrereqs.get(t)
    if (prereqs) {
      const sortedPrereqs = Array.from(prereqs)
      sortedPrereqs.sort((tid1, tid2) => comparePriorities(project, tid1, tid2))
      for (const prereq of sortedPrereqs) {
        if (!solveTask(prereq)) {
          // If we're here, we've failed to solve the completion date of this
          // prerequisite. We can't solve the completion date of other
          // preqrequisites since they're lower priorities and should be done
          // after higher priorities.
          return false
        }
      }
    }

    const duration = taskDuration(t)
    if (duration === null) return false

    time += duration
    solvedAt.set(t, time)
    return true
  }

  solveTask(META_TASK_ID)

  // Copy solve times over to project.
  for (const [id, task] of Object.entries(project.tasks)) {
    const solveTime = solvedAt.get(id)
    if (solveTime === undefined) {
      task.estimatedCompletion = null
    } else {
      task.estimatedCompletion = makeDate(begin, solveTime)
    }
  }
}
